import { useCallback, useEffect, useLayoutEffect } from 'react';
import { isEmpty, isNil } from 'lodash';
import { useCurrentValueGetter } from 'hooks';
import { AllEntityId } from 'modules/campaigns/config/allEntityId';
import { useTracerContext } from 'modules/taboola-common-frontend-modules/tracer/TracerProvider';
import { SPAN_SCOPE } from 'modules/taboola-common-frontend-modules/tracer/config/spanScope';
import { useIsTracerPermitted } from 'modules/taboola-common-frontend-modules/tracer/hooks/useIsTracerPermitted';
import { TRACER_STATUS_CODE } from 'modules/taboola-common-frontend-modules/tracer/utils/statusCode';
import { COMPONENT_STATUS } from 'services/constants';
import { ENTITY } from '../../../config';
import { useReportEntityType } from '../../../hooks/useReportEntityType';
import { gridMeasurement } from '../utils/gridMeasurement';

export const SPAN_EVENT_TYPES = {
    GRID_MEASUREMENT: 'GRID_MEASUREMENT',
    GRAPH_MEASUREMENT: 'GRAPH_MEASUREMENT',
};

const MEASURE_EXPIRATION_TIME = 1800000;
const emptyFilters = [];

export const useReportTracer = ({
    graphDataStatus,
    reportDataStatus,
    reportData,
    graphData,
    selectedReport,
    activeReportFilters = emptyFilters,
    datePreset,
    selectedAccount,
    selectedEntityIds,
}) => {
    const isTracerPermitted = useIsTracerPermitted();
    const [entityType] = useReportEntityType();

    const accountType = selectedAccount?.type;

    const {
        createRootSpan,
        getRootSpan,
        endRootSpan,
        startSpan,
        childSpansRef,
        getSpan,
        endSpan,
        setSpanStatus,
        stopMeasurement,
    } = useTracerContext();
    const selectedReportGetter = useCurrentValueGetter(selectedReport);

    const endSpanHandler = useCallback(() => {
        if (!isTracerPermitted) {
            return;
        }
        const activeSpan = getRootSpan(SPAN_SCOPE.REPORT_SCOPE);
        if (!activeSpan) {
            return;
        }

        if (childSpansRef.current.size === 0) {
            endRootSpan(SPAN_SCOPE.REPORT_SCOPE);
        }
    }, [childSpansRef, endRootSpan, getRootSpan, isTracerPermitted]);

    useLayoutEffect(() => {
        if (!isTracerPermitted) {
            return;
        }
        //start measure
        const rootSpan = getRootSpan(SPAN_SCOPE.REPORT_SCOPE);
        const { selectedReport } = rootSpan?.attributes || {};
        if (!isNil(selectedReport) && selectedReport !== selectedReportGetter()) {
            setSpanStatus(rootSpan, TRACER_STATUS_CODE.UNSET);
            endRootSpan(SPAN_SCOPE.REPORT_SCOPE);
        }
        const graphSpan = getSpan(SPAN_EVENT_TYPES.GRAPH_MEASUREMENT);
        const gridSpan = getSpan(SPAN_EVENT_TYPES.GRID_MEASUREMENT);
        if (isNil(rootSpan) && (!isNil(graphSpan) || !isNil(gridSpan))) {
            endSpan(SPAN_EVENT_TYPES.GRID_MEASUREMENT);
            endSpan(SPAN_EVENT_TYPES.GRAPH_MEASUREMENT);
        }
        if (
            isNil(rootSpan) &&
            (reportDataStatus === COMPONENT_STATUS.INITIAL ||
                graphDataStatus === COMPONENT_STATUS.INITIAL ||
                graphDataStatus === COMPONENT_STATUS.LOADING)
        ) {
            const span = createRootSpan(SPAN_SCOPE.REPORT_SCOPE, {
                attributes: {
                    reportId: selectedReportGetter(),
                    dateRange: datePreset,
                    reportFilterValues: activeReportFilters
                        .map(({ type }) => type)
                        .sort()
                        .join(','),
                    filters: isEmpty(activeReportFilters) ? undefined : 'ANY_FILTERS',
                    campaignFilter: entityType === ENTITY.CAMPAIGN && selectedEntityIds !== AllEntityId,
                },
            });

            const measureProps = {
                eventType: SPAN_EVENT_TYPES.GRID_MEASUREMENT,
                selectedReport: selectedReportGetter(),
                endSpanHandler,
                endSpan,
            };
            gridMeasurement(measureProps);

            startSpan(SPAN_EVENT_TYPES.GRID_MEASUREMENT, span, {
                selectedReport: selectedReportGetter(),
            });
            const timeoutId = setTimeout(() => {
                setSpanStatus(span, TRACER_STATUS_CODE.ERROR, 'Timeout');
                console.log('Tracer Timeout');
                endRootSpan(SPAN_SCOPE.REPORT_SCOPE);
            }, MEASURE_EXPIRATION_TIME);
            return () => clearTimeout(timeoutId);
        }
    }, [
        getRootSpan,
        reportDataStatus,
        graphDataStatus,
        createRootSpan,
        selectedReportGetter,
        endSpanHandler,
        endRootSpan,
        startSpan,
        endSpan,
        activeReportFilters,
        setSpanStatus,
        isTracerPermitted,
        getSpan,
        datePreset,
        selectedAccount,
        accountType,
        selectedEntityIds,
        entityType,
    ]);

    useEffect(() => {
        if (!isTracerPermitted) {
            return;
        }
        // add graph measure
        const parentSpan = getRootSpan(SPAN_SCOPE.REPORT_SCOPE);
        const graphSpan = getSpan(SPAN_EVENT_TYPES.GRAPH_MEASUREMENT);
        if (
            isNil(graphSpan) &&
            (graphDataStatus === COMPONENT_STATUS.INITIAL || graphDataStatus === COMPONENT_STATUS.LOADING)
        ) {
            startSpan(SPAN_EVENT_TYPES.GRAPH_MEASUREMENT, parentSpan, {
                selectedReport: selectedReportGetter(),
            });
        }

        if (
            graphDataStatus === COMPONENT_STATUS.ACTIVE ||
            graphDataStatus === COMPONENT_STATUS.ERROR ||
            graphDataStatus === COMPONENT_STATUS.EMPTY
        ) {
            const activeSpan = getSpan(SPAN_EVENT_TYPES.GRAPH_MEASUREMENT);
            if (graphDataStatus === COMPONENT_STATUS.ERROR) {
                setSpanStatus(activeSpan, TRACER_STATUS_CODE.ERROR, `Graph data status: ${graphDataStatus}`);
            }
            if (isEmpty(graphData)) {
                setSpanStatus(activeSpan, TRACER_STATUS_CODE.UNSET, 'No available Graph Data');
            }
            endSpan(SPAN_EVENT_TYPES.GRAPH_MEASUREMENT);
            endSpanHandler();
        }
    }, [
        endSpan,
        endSpanHandler,
        getRootSpan,
        getSpan,
        graphDataStatus,
        selectedReportGetter,
        setSpanStatus,
        startSpan,
        isTracerPermitted,
        graphData,
    ]);

    useEffect(() => {
        if (!isTracerPermitted) {
            return;
        }
        //stop grid measure
        const activeSpan = getSpan(SPAN_EVENT_TYPES.GRID_MEASUREMENT);
        if (reportDataStatus === COMPONENT_STATUS.ACTIVE) {
            if (isEmpty(reportData)) {
                setSpanStatus(activeSpan, TRACER_STATUS_CODE.UNSET, 'No available Report Data');
                endSpan(SPAN_EVENT_TYPES.GRID_MEASUREMENT);
            }
            endSpanHandler();
        }
        if (reportDataStatus === COMPONENT_STATUS.ERROR || reportDataStatus === COMPONENT_STATUS.EMPTY) {
            setSpanStatus(activeSpan, TRACER_STATUS_CODE.ERROR, `Report data status: ${reportDataStatus}`);
            endRootSpan(SPAN_SCOPE.REPORT_SCOPE);
        }
    }, [
        reportDataStatus,
        selectedReportGetter,
        endRootSpan,
        getRootSpan,
        endSpanHandler,
        graphDataStatus,
        setSpanStatus,
        getSpan,
        isTracerPermitted,
        reportData,
        endSpan,
    ]);

    useEffect(() => {
        return () => {
            stopMeasurement();
        };
    }, [stopMeasurement]);
};
